import React, { useState } from "react";
import { Box } from "@mui/material";
import { Add as AddIcon, AlarmOff as TimeOffIcon } from "@mui/icons-material";
import { endOfYear, startOfYear } from "date-fns";
import { PageTitle, DataGrid, Tiles, FilterBar } from "../../components";
import { useResponsive, useUser } from "../../hooks";
import { Country } from "../../API";
import { useRequestModals } from "./Modals";
import { columns, hiddenColumnsMobile } from "./columns";
import { useTimeOffQuery } from "./useTimeOffQuery";

const TEST_ID = "time-off";

export const TimeOff = () => {
  const user = useUser();
  const { showCreateModal, showUpdateModal, showDeleteModal } =
    useRequestModals();
  const { isMd } = useResponsive();

  const [filterUser, setFilterUser] = useState<string>(user?.id ?? "");
  const [dates, setDates] = useState<Interval | null>({
    start: startOfYear(new Date()),
    end: endOfYear(new Date()),
  });

  const { items, error, isPending, daysUsed, vacationAllowance, country } =
    useTimeOffQuery(filterUser, dates);
  const remaining = (vacationAllowance ?? 0) - (daysUsed ?? 0);

  const handleUserSearch = (val: string) => {
    setFilterUser(val ? val : user?.id ?? "");
  };

  const fileName = `${
    filterUser ? `${filterUser}_${Date.now()}_` : ""
  }TimeOff_Report`;

  return (
    <>
      <PageTitle
        action={{
          label: "Time Off",
          icon: <AddIcon />,
          onClick: () =>
            showCreateModal({
              maxAmount: remaining,
              absences: items,
              country: country ?? Country.GB,
              employeeID: filterUser,
              maxDate: dates?.end ? new Date(dates.end) : new Date(),
              minDate: dates?.start ? new Date(dates.start) : new Date(),
            }),
          disabled: remaining <= 0,
        }}
        icon={<TimeOffIcon />}
        title="Time Off"
        testID={TEST_ID}
      />

      <FilterBar
        employee={
          user && user?.isAdmin
            ? { value: filterUser || user.id, onChange: handleUserSearch }
            : undefined
        }
        dates={{
          value: dates,
          onChange: setDates,
          type: "year",
        }}
        testID="timeoff"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            sm: "repeat(3, 1fr)",
            md: "repeat(4, 1fr)",
            lg: "repeat(5, 1fr)",
            xl: "repeat(6, 1fr)",
          },
          alignItems: "center",
          gap: 2,
          mb: 1,
        }}
        data-testid={`${TEST_ID}-tiles`}
      >
        <Tiles
          items={[
            `Allowance: ${vacationAllowance ?? 0}`,
            `Used: ${daysUsed ?? 0}`,
            `Remaining: ${remaining}`,
          ]}
        />
      </Box>

      <DataGrid
        columns={columns({
          showUpdateModal,
          showDeleteModal,
          remaining,
          absences: items,
          vacationAllowance,
          country: country ?? Country.GB,
          employeeID: filterUser,
          maxDate: dates?.end ? new Date(dates.end) : new Date(),
          minDate: dates?.start ? new Date(dates.start) : new Date(),
        })}
        data={items ?? []}
        loading={isPending}
        csvOptions={{ fileName }}
        emptyState={{
          title: "Time Off",
          description: error?.message ?? "You have no time off requests.",
          testID: TEST_ID,
        }}
        hideColumnSelector
        hideExport={user?.isAdmin === true ? false : true}
        testID={TEST_ID}
        disableColumnMenu
        hideFooterPagination
        disableSelectionOnClick
        columnVisibility={isMd ? hiddenColumnsMobile : {}}
        sortModel={[
          {
            field: "startDate",
            sort: "asc",
          },
        ]}
      />
    </>
  );
};
